import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Circle, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, imgSrc } from "@/lib/api";
import { ImageUpload } from "@/components/ImageUpload";
import { useAuth } from "@/context/AuthContext";

const AddLesson = ({ courseId, onAdded }) => {
  const [f, setF] = useState({ title: "", youtube_url: "", description: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      onAdded((await api.post(`/courses/${courseId}/lessons`, f)).data);
      setF({ title: "", youtube_url: "", description: "" });
      toast.success("Lesson added");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={save} className="hb-card p-5 space-y-3 bg-[var(--hb-lime-soft)]" data-testid="add-lesson-form">
      <div className="label-accent !text-[var(--hb-ink)]">Add a lesson</div>
      <input className="hb-input" placeholder="Lesson title" required value={f.title} onChange={set("title")} data-testid="lesson-title-input" />
      <input className="hb-input" placeholder="Paste YouTube link (unlisted works)" required value={f.youtube_url} onChange={set("youtube_url")} data-testid="lesson-url-input" />
      <textarea className="hb-input" placeholder="Notes (optional)" value={f.description} onChange={set("description")} data-testid="lesson-description-input" />
      <button className="btn-dark w-full" data-testid="lesson-save-button"><Plus size={14} /> Add lesson</button>
    </form>
  );
};

export default function CoursePage() {
  const { courseId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user.role === "admin";
  const [course, setCourse] = useState(null);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    api.get(`/courses/${courseId}`).then((r) => {
      setCourse(r.data);
      setActiveId(r.data.lessons[0]?.id || null);
    }).catch(() => navigate("/classroom"));
  }, [courseId, navigate]);

  if (!course) return <div className="text-[var(--hb-muted)]">Loading course...</div>;
  const lessons = course.lessons;
  const active = lessons.find((l) => l.id === activeId);
  const setLessons = (ls) => setCourse({ ...course, lessons: ls });

  const safe = (fn) => async (...args) => {
    try {
      await fn(...args);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const toggle = safe(async () => {
    const { data } = await api.post(`/lessons/${active.id}/complete`);
    setLessons(lessons.map((l) => (l.id === active.id ? { ...l, completed: data.completed } : l)));
  });
  const removeLesson = safe(async (l) => {
    if (!window.confirm(`Delete "${l.title}"?`)) return;
    await api.delete(`/lessons/${l.id}`);
    const rest = lessons.filter((x) => x.id !== l.id);
    setLessons(rest);
    if (activeId === l.id) setActiveId(rest[0]?.id || null);
  });
  const removeCourse = safe(async () => {
    if (!window.confirm("Delete this whole course and its lessons?")) return;
    await api.delete(`/courses/${course.id}`);
    navigate("/classroom");
  });
  const changeCover = safe(async (url) => {
    const { data } = await api.put(`/courses/${course.id}`, { title: course.title, description: course.description, cover_url: url });
    setCourse({ ...course, cover_url: data.cover_url });
    toast.success("Cover updated");
  });
  const done = lessons.filter((l) => l.completed).length;

  return (
    <div data-testid="course-page">
      <div className="flex items-center justify-between">
        <Link to="/classroom" className="inline-flex items-center gap-2 text-sm text-[var(--hb-muted)] hover:text-[var(--hb-ink)]" data-testid="course-back-link">
          <ArrowLeft size={15} /> Classroom
        </Link>
        {isAdmin && (
          <div className="flex gap-2">
            <ImageUpload testId="course-change-cover" label={course.cover_url ? "Change cover" : "Add cover"} onUploaded={changeCover} />
            <button className="btn-ghost" onClick={removeCourse} data-testid="course-delete-button"><Trash2 size={13} /> Delete course</button>
          </div>
        )}
      </div>
      <div className="flex items-center gap-4 mt-3">
        {course.cover_url && (
          <img src={imgSrc(course.cover_url)} alt="Course cover" className="w-24 aspect-video object-cover rounded-xl border border-[var(--hb-line)]" data-testid="course-cover-image" />
        )}
        <h1 className="font-display text-3xl sm:text-4xl" data-testid="course-title">{course.title}</h1>
      </div>
      <div className="grid lg:grid-cols-[1fr_340px] gap-8 mt-6">
        <section className="min-w-0">
          {active ? (
            <>
              <div className="aspect-video rounded-2xl overflow-hidden bg-black" data-testid="lesson-player">
                <iframe
                  key={active.id}
                  className="w-full h-full"
                  src={`https://www.youtube-nocookie.com/embed/${active.youtube_id}?rel=0&modestbranding=1`}
                  title={active.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="flex flex-wrap items-start justify-between gap-4 mt-5">
                <h2 className="font-display text-2xl" data-testid="lesson-title">{active.title}</h2>
                <button onClick={toggle} className={active.completed ? "btn-dark" : "btn-primary"} data-testid="lesson-complete-toggle">
                  <CheckCircle2 size={14} /> {active.completed ? "Completed" : "Mark complete"}
                </button>
              </div>
              {active.description && <p className="mt-3 whitespace-pre-wrap leading-relaxed">{active.description}</p>}
            </>
          ) : (
            <div className="hb-card aspect-video grid place-items-center text-center p-8" data-testid="course-no-lessons">
              <div>
                <p className="font-display text-xl">No lessons yet.</p>
                {isAdmin && <p className="text-sm text-[var(--hb-muted)] mt-1">Paste your first YouTube link on the right.</p>}
              </div>
            </div>
          )}
        </section>
        <aside className="space-y-5">
          <div className="hb-card p-4">
            <div className="flex items-center justify-between px-1 pb-3">
              <span className="label-accent">Lessons</span>
              <span className="font-accent text-xs font-bold" data-testid="course-done-count">{done}/{lessons.length}</span>
            </div>
            <div className="space-y-1">
              {lessons.map((l, i) => (
                <div key={l.id} className={`flex items-center gap-2 rounded-xl px-3 py-2.5 cursor-pointer transition-colors ${l.id === activeId ? "bg-[var(--hb-lime-soft)]" : "hover:bg-[#F5F5F0]"}`}
                  onClick={() => setActiveId(l.id)} data-testid={`lesson-item-${l.id}`}>
                  {l.completed ? <CheckCircle2 size={17} className="text-[var(--hb-ink)] fill-[var(--hb-lime)]" /> : <Circle size={17} className="text-[var(--hb-muted)]" />}
                  <span className="text-sm flex-1">{i + 1}. {l.title}</span>
                  {isAdmin && (
                    <button onClick={(e) => { e.stopPropagation(); removeLesson(l); }} className="text-[var(--hb-muted)] hover:text-red-600" data-testid={`lesson-delete-${l.id}`}>
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
          {isAdmin && <AddLesson courseId={course.id} onAdded={(l) => { setLessons([...lessons, { ...l, completed: false }]); setActiveId(l.id); }} />}
        </aside>
      </div>
    </div>
  );
}
